import {} from '@synergy/components-next';
import dayjs, { Dayjs } from 'dayjs';
import { Box, Divider, Grid, Stack, Switch, Tooltip } from '@mui/material';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { ImportStatus } from '@synergy/models';
import { InformationOutlinedIcon } from '@synergy/theme-next';
import { PoolButton, PoolDatePicker, PoolTypography } from '@synergy/components';
import { makeStyles } from '@mui/styles';

import { ImportDetailTabs } from '../../models/import-detail-tabs';
import {
    computeItemChanges,
    retryFailed,
    retryWarning,
    setPublishDate,
    toggleKeepSpecialPrices,
    toggleShouldDeleteExisting,
    toggleShouldNotifySupplier,
    toggleShouldOverwriteFiles,
    toggleShouldSkipCategoryCreation,
} from '../../store/all/import-actions';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles(() => ({
    content: {
        padding: '22px 32px 22px',
    },
    navItemText: {
        fontSize: 15,
        fontWeight: 'bold',
        color: '#21272e',
    },
    responsibleContact: {
        fontSize: 22,
        marginBottom: 32,
    },
}));

interface ImportDetailSidebarProps {
    selectedTab: ImportDetailTabs;
    importJobId: number;
}

export const ImportDetailSidebar = (props: ImportDetailSidebarProps) => {
    const classes = useStyles();
    const dispatch = useThunkDispatch();
    const { importDetails } = useAppSelector(state => state.import);
    const { selectedTab, importJobId } = props;
    const tomorrow = dayjs().add(1, 'day');

    const handleNewCalculation = () => {
        dispatch(computeItemChanges(importJobId));
    };

    const handleRetryFailed = () => {
        dispatch(retryFailed(importJobId));
    };

    const handleRetryWarning = () => {
        dispatch(retryWarning(importJobId));
    };

    const handleShouldDeleteExistingChange = () => {
        dispatch(toggleShouldDeleteExisting(importJobId, !importDetails?.shouldDeleteExisting));
    };

    const handleShouldSkipCategoryCreation = () => {
        dispatch(toggleShouldSkipCategoryCreation(importJobId, !importDetails?.shouldSkipCategoryCreation));
    };

    const handleKeepSpecialPricesChange = () => {
        dispatch(toggleKeepSpecialPrices(importJobId, !importDetails?.keepSpecialPrices));
    };

    const handleShouldOverwriteFiles = () => {
        dispatch(toggleShouldOverwriteFiles(importJobId, !importDetails?.shouldOverwriteFiles));
    };

    const handleShouldNotifySupplierChange = () => {
        dispatch(toggleShouldNotifySupplier(importJobId, !importDetails?.shouldNotifySupplier));
    };

    const handlePublishDateChange = (value: Dayjs | null) => {
        dispatch(setPublishDate(importJobId, value ? value.startOf('day').format('YYYY-MM-DD') : null));
    };

    const isCalculationEnabled =
        (importDetails?.itemCount ?? 0) > 0 &&
        importDetails?.status &&
        [ImportStatus.ReadyToPublish, ImportStatus.Published].includes(importDetails?.status);

    const hasFailedItems = importDetails && importDetails?.failedItemCount > 0;
    const hasWarningItems = importDetails && importDetails?.warningItemCount > 0;
    const canSettingsBeUpdated =
        importDetails?.status === ImportStatus.Scheduled || importDetails?.status === ImportStatus.ReadyToPublish;

    return (
        <Grid container={true} spacing={3} className={classes.content}>
            <Grid container={true} item={true} spacing={4} direction="column">
                {importDetails?.supplierAccount && (
                    <Grid container={true} item={true} spacing={1} direction="column">
                        <Grid item={true}>
                            <PoolTypography typography="subtitle1">
                                {importDetails?.supplierAccount.name}
                            </PoolTypography>
                        </Grid>
                        {importDetails?.supplierAccount.email && (
                            <Grid item={true}>
                                <PoolTypography>{importDetails.supplierAccount.email}</PoolTypography>
                            </Grid>
                        )}
                        {importDetails?.supplierAccount.phone && (
                            <Grid item={true}>
                                <PoolTypography>{importDetails.supplierAccount.phone}</PoolTypography>
                            </Grid>
                        )}
                    </Grid>
                )}

                {importDetails?.responsibleContact && (
                    <Grid container={true} item={true} spacing={1} direction="column">
                        <Grid item={true}>
                            <PoolTypography typography="subtitle1" id="app.common.responsibleContact" />
                        </Grid>
                        <Grid item={true}>
                            <PoolTypography>{importDetails.responsibleContact.fullName}</PoolTypography>
                        </Grid>
                    </Grid>
                )}

                <Grid container={true} item={true} spacing={1} direction="column">
                    <Grid item={true}>
                        <PoolTypography typography="subtitle1" id="app.table.column.createdAt" />
                    </Grid>
                    <Grid item={true}>
                        <PoolTypography>
                            <FormattedDate
                                value={importDetails?.createdAt}
                                year="numeric"
                                month="short"
                                day="2-digit"
                                hour="numeric"
                                minute="numeric"
                                hour12={false}
                            />
                            &nbsp;
                            <FormattedMessage id="app.common.clock" />
                        </PoolTypography>
                    </Grid>
                </Grid>

                <Grid container={true} item={true} gap={0.5} direction="column">
                    <Grid item={true}>
                        <PoolTypography typography="subtitle1" id="app.table.column.settings" />
                    </Grid>
                    <PoolDatePicker
                        name="publishDate"
                        label={<FormattedMessage id="app.imports.detail.publishDate" />}
                        value={importDetails?.publishDate ? dayjs(importDetails?.publishDate) : null}
                        onChange={handlePublishDateChange}
                        disabled={!canSettingsBeUpdated}
                        minDate={importDetails?.status && canSettingsBeUpdated ? tomorrow : undefined}
                        sx={{ marginTop: 1.5 }}
                    />
                    <Grid item={true}>
                        {importDetails?.autoPublish && (
                            <>
                                <Stack direction="row" alignItems="center" ml={-1} mb={-0.5} py={1} height={44}>
                                    <Switch checked={true} color="primary" disabled={true} />
                                    <PoolTypography typography="body1" id="app.imports.detail.autoPublish" />
                                </Stack>
                                <Divider />
                            </>
                        )}
                        <Stack direction="row" alignItems="center" ml={-1} py={1} height={44}>
                            <Switch
                                onChange={handleShouldDeleteExistingChange}
                                checked={importDetails?.shouldDeleteExisting ?? false}
                                color="primary"
                                disabled={!canSettingsBeUpdated}
                            />
                            <PoolTypography typography="body1" id="app.imports.detail.shouldDeleteExisting" />
                        </Stack>
                        <Divider />
                        <Stack direction="row" alignItems="center" ml={-1} mb={-0.5} py={1} height={44}>
                            <Switch
                                onChange={handleShouldSkipCategoryCreation}
                                checked={importDetails?.shouldSkipCategoryCreation ?? true}
                                color="primary"
                                disabled={!canSettingsBeUpdated}
                            />
                            <PoolTypography typography="body1" id="app.imports.detail.shouldSkipCategoryCreation" />
                        </Stack>
                        <Divider />
                        <Stack direction="row" alignItems="center" ml={-1} py={1} height={44}>
                            <Switch
                                onChange={handleKeepSpecialPricesChange}
                                checked={importDetails?.keepSpecialPrices ?? false}
                                color="primary"
                                disabled={!canSettingsBeUpdated}
                            />
                            <PoolTypography typography="body1" id="app.imports.detail.keepSpecialPrices" />
                        </Stack>
                        {importDetails?.showNotifySupplierToggle && (
                            <Stack direction="row" alignItems="center" ml={-1} py={1} height={44}>
                                <Switch
                                    onChange={handleShouldNotifySupplierChange}
                                    checked={importDetails?.shouldNotifySupplier ?? false}
                                    color="primary"
                                    disabled={!canSettingsBeUpdated}
                                />
                                <PoolTypography typography="body1" id="app.imports.detail.notifySupplier" />

                                <Tooltip title={<FormattedMessage id="app.toolTip.notifySupplier" />} placement="top">
                                    <Box ml={1}>
                                        <InformationOutlinedIcon sx={{ fontSize: 18 }} />
                                    </Box>
                                </Tooltip>
                            </Stack>
                        )}

                        <Stack direction="row" alignItems="center" ml={-1} py={1} height={44}>
                            <Switch
                                onChange={handleShouldOverwriteFiles}
                                checked={importDetails?.shouldOverwriteFiles ?? false}
                                color="primary"
                                disabled={!canSettingsBeUpdated}
                            />
                            <PoolTypography typography="body1" id="app.imports.detail.overwriteFiles" />

                            <Tooltip title={<FormattedMessage id="app.toolTip.overwriteFiles" />} placement="top">
                                <Box ml={1}>
                                    <InformationOutlinedIcon sx={{ fontSize: 18 }} />
                                </Box>
                            </Tooltip>
                        </Stack>
                    </Grid>
                </Grid>

                <Grid container={true} item={true} spacing={1} direction="column">
                    <Grid item={true}>
                        {importDetails?.isImageBasePathExternal ? (
                            <PoolTypography typography="subtitle1" id="app.imports.detail.imageBasePath.external" />
                        ) : (
                            <PoolTypography typography="subtitle1" id="app.imports.detail.imageBasePath.s3" />
                        )}
                    </Grid>
                    <Grid item={true} sx={{ overflowWrap: 'anywhere' }}>
                        <PoolTypography>{importDetails?.imageBasePath}</PoolTypography>
                    </Grid>
                </Grid>

                <Grid item={true}>
                    <Divider />
                </Grid>
            </Grid>

            <Grid container={true} item={true} spacing={2} style={{ paddingRight: 48 }}>
                <Grid item={true} style={{ paddingBottom: 6 }} xs={12}>
                    <Stack flexDirection="row" gap={1} alignItems="end">
                        <PoolTypography typography="h6" id="app.imports.detail.statistics" />
                        <PoolTypography
                            typography="body1"
                            id="app.imports.detail.receivedArticles"
                            values={{ receivedArticles: importDetails?.itemCount }}
                        />
                    </Stack>
                </Grid>

                <Grid container={true} item={true} xs={6} direction="column">
                    <Grid item={true} style={{ paddingBottom: 4 }}>
                        <PoolTypography typography="h5">
                            {importDetails?.status === ImportStatus.Published
                                ? importDetails?.productsNewCount || 0
                                : importDetails?.changeStateNewCount || 0}
                        </PoolTypography>
                    </Grid>
                    <Grid item={true}>
                        <PoolTypography typography="caption" id="app.imports.detail.new" />
                    </Grid>
                </Grid>

                <Grid container={true} item={true} xs={6} direction="column">
                    <Grid item={true} style={{ paddingBottom: 4 }}>
                        <PoolTypography typography="h5">
                            {importDetails?.status === ImportStatus.Published
                                ? importDetails?.productsChangedCount || 0
                                : importDetails?.changeStateChangedCount || 0}
                        </PoolTypography>
                    </Grid>
                    <Grid item={true}>
                        <PoolTypography
                            typography="caption"
                            id={
                                importDetails?.status === ImportStatus.Published
                                    ? 'app.imports.detail.changed'
                                    : 'app.imports.detail.toChange'
                            }
                        />
                    </Grid>
                </Grid>

                <Grid container={true} item={true} xs={6} direction="column">
                    <Grid item={true} style={{ paddingBottom: 4 }}>
                        <PoolTypography typography="h5">
                            {importDetails?.status === ImportStatus.Published
                                ? importDetails?.productsDeletedCount || 0
                                : importDetails?.productsToBeDeleted || 0}
                        </PoolTypography>
                    </Grid>
                    <Grid item={true}>
                        <PoolTypography
                            typography="caption"
                            id={
                                importDetails?.status === ImportStatus.Published
                                    ? 'app.imports.detail.delete'
                                    : 'app.imports.detail.toDelete'
                            }
                        />
                    </Grid>
                </Grid>

                <Grid container={true} item={true} xs={6} direction="column">
                    <Grid item={true} style={{ paddingBottom: 4 }}>
                        <PoolTypography typography="h5">
                            {importDetails?.status === ImportStatus.Published
                                ? importDetails?.productsUnchangedCount || 0
                                : importDetails?.changeStateUnchangedCount || 0}
                        </PoolTypography>
                    </Grid>
                    <Grid item={true}>
                        <PoolTypography typography="caption" id="app.imports.detail.unchanged" />
                    </Grid>
                </Grid>
            </Grid>

            {selectedTab === ImportDetailTabs.Imported && (
                <Grid item={true}>
                    <PoolButton styleType="firstLevel" onClick={handleNewCalculation} disabled={!isCalculationEnabled}>
                        <FormattedMessage id="app.imports.detail.restartCalculation" />
                    </PoolButton>
                </Grid>
            )}

            {(selectedTab === ImportDetailTabs.Failed || selectedTab === ImportDetailTabs.Warning) && (
                <>
                    <Grid container={true} item={true} spacing={4} direction="column">
                        <Grid item={true}>
                            <Divider />
                        </Grid>
                    </Grid>

                    <Grid container={true} item={true} spacing={4} direction="column">
                        <Grid item={true}>
                            <PoolTypography
                                typography="h6"
                                id={`app.imports.detail.${
                                    selectedTab === ImportDetailTabs.Failed ? 'failedArticles' : 'warningArticles'
                                }`}
                            />
                        </Grid>

                        <Grid item={true}>
                            {selectedTab === ImportDetailTabs.Failed && (
                                <PoolButton
                                    styleType="firstLevel"
                                    onClick={handleRetryFailed}
                                    disabled={!hasFailedItems}
                                >
                                    <FormattedMessage id="app.imports.detail.retryFailed" />
                                </PoolButton>
                            )}
                            {selectedTab === ImportDetailTabs.Warning && (
                                <PoolButton
                                    styleType="firstLevel"
                                    onClick={handleRetryWarning}
                                    disabled={!hasWarningItems}
                                >
                                    <FormattedMessage id="app.imports.detail.retryFailed" />
                                </PoolButton>
                            )}
                        </Grid>
                    </Grid>
                </>
            )}
        </Grid>
    );
};
